import AddIcon from '@mui/icons-material/Add';
import { Grid } from '@mui/material';
import { HSpacer, RoutalPalette } from '@nx-smartmonkey/ui';

import { useState } from 'react';
import { Project } from '../../../../domain/project/Project';
import { ProjectDriversInputField } from '../../../../domain/project/projectDrivers/ProjectDriversEntityField';
import {
  ProjectDriversReportCompleteType,
  projectDriversReportCompleteValues,
} from '../../../../domain/project/projectDrivers/ProjectDriversFields';
import { intl } from '../../../../helpers/IntGlobalProvider';
import { useUpdateProject } from '../../../../hooks/project/useUpdateProject';
import { ProjectDriversMapper } from '../../../../mappers/project/ProjectDriversMapper';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';
import { AppField, ManageFieldsDialog } from '../../components/ManageFieldsDialog';
import { Tag } from '../../components/Tag';
import { SortableTableOptionContainer } from '../../components/sortable-table/SortableTableOptionContainer';
import { SortableTableOptionPillItem } from '../../components/sortable-table/SortableTableOptionPill';

interface StopFieldsProps {
  project: Project;
}

export const StopFields = ({ project }: StopFieldsProps) => {
  const { run: editProject } = useUpdateProject({ project });
  const [manageFieldsDialogOpen, setManageFieldsDialogOpen] = useState<boolean>(false);

  const getAllFields = (): AppField[] => {
    const fields: AppField[] = Object.entries(projectDriversReportCompleteValues).map(([id, type]) => {
      const foundField = project.drivers.stop_completed_fields.find((f) => f.id === id);

      return {
        id: id,
        text: intl.formatMessage({ id: `drivers.pod.stop_completed_fields.${id}` }),
        type: type as ProjectDriversReportCompleteType,
        is_custom_field: false,
        is_required: Boolean(foundField?.is_required),
      };
    });

    project.custom_fields?.service_report_completed.forEach((cField) => {
      const foundField = project.drivers.stop_completed_fields.find(
        (f) => f.is_custom_field && f.id === cField.custom_id
      );

      let type: ProjectDriversReportCompleteType;
      switch (cField.field_type) {
        case `numerical`:
          type = `number`;
          break;
        case `categorical`: {
          if (cField.multiple) {
            type = `multiselect`;
            break;
          }
          type = `select`;
          break;
        }
        default:
          type = `string`;
          break;
      }

      fields.push({
        id: cField.custom_id,
        text: cField.label,
        type,
        is_custom_field: true,
        is_required: Boolean(foundField?.is_required),
      });
    });

    return fields;
  };

  const getAllActiveFields = (): AppField[] => {
    return project.drivers.stop_completed_fields
      .filter((field) => {
        // Filtering the fields that are enabled in project view.client or constraints
        if (!field.is_custom_field) return true;

        // Search for the custom field in the custom fields list
        const cFieldFound = project.custom_fields?.service_report_completed.find(
          (cField) => cField.custom_id === field.id
        );
        return Boolean(cFieldFound);
      })
      .map((field) => {
        if (!field.is_custom_field) {
          return {
            id: field.id,
            text: intl.formatMessage({ id: `drivers.pod.stop_completed_fields.${field.id}` }),
            type: field.type,
            is_custom_field: false,
            is_required: field.is_required,
          };
        }

        const cFieldFound = project.custom_fields?.service_report_completed.find(
          (cField) => cField.custom_id === field.id
        );

        return {
          id: cFieldFound!.custom_id,
          text: cFieldFound!.label ?? ``,
          type: field.type,
          is_custom_field: true,
          is_required: field.is_required,
        };
      });
  };

  const getUnassignedFields = (): AppField[] => {
    return getAllFields().filter(
      (field) => !getAllActiveFields().find((f) => f.id === field.id && f.is_custom_field === field.is_custom_field)
    );
  };

  return (
    <>
      <SortableTableOptionContainer
        listId="drivers.pod.stop_completed_fields"
        onDragEnd={(fieldId: string, index?: number) => {
          if (index !== undefined) {
            const projectDrivers = ProjectDriversMapper.toDTO(project.drivers);

            const fieldIndex = projectDrivers.stop_completed_fields.findIndex((field) => field.id === fieldId);
            const field = projectDrivers.stop_completed_fields[fieldIndex];

            projectDrivers.stop_completed_fields.splice(fieldIndex, 1);
            projectDrivers.stop_completed_fields.splice(index, 0, field);

            editProject({
              drivers: ProjectDriversMapper.toDomain({
                ...projectDrivers,
                stop_completed_fields: projectDrivers.stop_completed_fields,
              }),
            });
          }
        }}
        emptyListMessage={
          <Grid container direction="row" alignItems="center" justifyContent="center" style={{ height: `55px` }}>
            <Text variant="label" intlId="drivers.no_fields" />
          </Grid>
        }
        fields={getAllActiveFields()}
        columns={[
          {
            id: `name`,
            width: `40%`,
            name: (
              <Text variant="label" style={{ color: RoutalPalette.primary20 }} intlId="drivers.fields.column.name" />
            ),
            formatter: ({ row }: { row: SortableTableOptionPillItem }) => {
              return (
                <Text variant="body" style={{ cursor: `grab` }}>
                  {row.text}
                </Text>
              );
            },
          },
          {
            id: `required`,
            width: `20%`,
            name: <Grid item container justifyContent="center" />,
            formatter: ({ row }: { row: SortableTableOptionPillItem }) => {
              if (!row.is_required) return null;
              return (
                <Grid item container justifyContent="center">
                  <Tag bgColor={RoutalPalette.secondary40} color={RoutalPalette.oldWhite}>
                    {intl.formatMessage({ id: `drivers.fields.required` })}
                  </Tag>
                </Grid>
              );
            },
          },
          {
            id: `custom_field`,
            width: `20%`,
            name: <Grid item container justifyContent="center" />,
            formatter: ({ row }: { row: SortableTableOptionPillItem }) => {
              if (!row.is_custom_field) return null;
              return (
                <Grid item container justifyContent="center">
                  <Tag bgColor={RoutalPalette.primary00} color={RoutalPalette.oldWhite}>
                    {intl.formatMessage({ id: `drivers.fields.custom` })}
                  </Tag>
                </Grid>
              );
            },
          },
          {
            id: `field_type`,
            width: `20%`,
            name: (
              <Grid item container justifyContent="center">
                <Text
                  variant="label"
                  style={{ color: RoutalPalette.primary20 }}
                  intlId="drivers.fields.column.field_type"
                />
              </Grid>
            ),
            formatter: ({ row }: { row: SortableTableOptionPillItem }) => {
              const stopField = getAllActiveFields().find((f) => f.id === row.id);
              if (!stopField) return null;
              return (
                <Grid item container justifyContent="center">
                  <Text
                    variant="body"
                    style={{ cursor: `grab` }}
                    intlId={`drivers.field_types.${stopField.type as ProjectDriversReportCompleteType}`}
                  />
                </Grid>
              );
            },
          },
        ]}
        buttonActions={[
          {
            key: `mark_as_required`,
            show: (field: SortableTableOptionPillItem) => !field.is_required,
            content: <Text style={{ cursor: `pointer` }} intlId="drivers.fields.actions.mark_as_required" />,
            onClick: (field: SortableTableOptionPillItem) => {
              const projectDrivers = ProjectDriversMapper.toDTO(project.drivers);

              const fieldIndex = projectDrivers.stop_completed_fields.findIndex((f) => f.id === field.id);
              projectDrivers.stop_completed_fields[fieldIndex].is_required = true;

              editProject({
                drivers: ProjectDriversMapper.toDomain({
                  ...projectDrivers,
                  stop_completed_fields: projectDrivers.stop_completed_fields,
                }),
              });
            },
          },
          {
            key: `mark_as_optional`,
            show: (field: SortableTableOptionPillItem) => Boolean(field.is_required),
            content: <Text style={{ cursor: `pointer` }} intlId="drivers.fields.actions.mark_as_optional" />,
            onClick: (field: SortableTableOptionPillItem) => {
              const projectDrivers = ProjectDriversMapper.toDTO(project.drivers);

              const fieldIndex = projectDrivers.stop_completed_fields.findIndex((f) => f.id === field.id);
              projectDrivers.stop_completed_fields[fieldIndex].is_required = false;

              editProject({
                drivers: ProjectDriversMapper.toDomain({
                  ...projectDrivers,
                  stop_completed_fields: projectDrivers.stop_completed_fields,
                }),
              });
            },
          },
          null,
          {
            key: `delete`,
            content: (
              <Text
                style={{ color: RoutalPalette.error.medium, cursor: `pointer` }}
                intlId="drivers.fields.actions.delete"
              />
            ),
            onClick: (field: SortableTableOptionPillItem) => {
              const projectDrivers = ProjectDriversMapper.toDTO(project.drivers);

              projectDrivers.stop_completed_fields = projectDrivers.stop_completed_fields.filter(
                (stopField) => stopField.id !== field.id
              );

              editProject({
                drivers: ProjectDriversMapper.toDomain({
                  ...projectDrivers,
                  stop_completed_fields: projectDrivers.stop_completed_fields,
                }),
              });
            },
          },
        ]}
      />

      <Grid container direction="row" justifyContent="flex-end" style={{ marginTop: `8px` }}>
        <Button disabled={getUnassignedFields().length === 0} onClick={() => setManageFieldsDialogOpen(true)}>
          <AddIcon />
          <HSpacer small />
          <Text intlId="drivers.manage_fields" style={{ color: RoutalPalette.neutral00, cursor: `pointer` }} />
        </Button>
      </Grid>

      <ManageFieldsDialog
        open={manageFieldsDialogOpen}
        onClose={() => setManageFieldsDialogOpen(false)}
        unassignedFields={getUnassignedFields()}
        onUpdate={({ newFields }) => {
          const projectDrivers = ProjectDriversMapper.toDTO(project.drivers);

          projectDrivers.stop_completed_fields = projectDrivers.stop_completed_fields.concat(
            newFields.map((field) => {
              const { text, ...rest } = field;
              return rest as ProjectDriversInputField<ProjectDriversReportCompleteType>;
            })
          );

          editProject({
            drivers: ProjectDriversMapper.toDomain(projectDrivers),
          });
        }}
      />
    </>
  );
};
